// Human-readable copy for a live (SSE) channel that has hard-failed, keyed by the HTTP
// status `subscribeSse`'s hard-close probe recovered (see api/sse.ts). A1EE-364.
//
// Pure and React-free so the mapping is unit-testable without a DOM: the wording IS the
// deliverable of A1EE-364 ("no human-readable explanation of what's wrong, no actionable
// hint"), so it deserves tests that don't depend on rendering.

// What the user should DO about it, which is not derivable from the status alone:
//  - 'reload'  the running SPA is talking to a backend that no longer speaks its protocol.
//              No amount of waiting fixes it; the page has to be re-fetched.
//  - 'wait'    the backend is transiently unhappy. The wrapper keeps retrying and will
//              recover on its own, so offering a reload button would be noise.
//  - 'access'  a permissions answer, not an outage. Reloading changes nothing.
export type SseFailureAdvice = 'reload' | 'wait' | 'access';

export interface SseFailureCopy {
  message: string;
  advice: SseFailureAdvice;
}

/**
 * Map a hard-failure HTTP status to user-facing copy.
 *
 * `status === null` means the probe got no HTTP response at all (DNS, offline, connection
 * refused). That is deliberately a DIFFERENT message from any status the server answered
 * with: "can't reach the server" is actionable by the user (check the network), whereas a
 * 404 is not.
 */
export function sseFailureCopy(status: number | null): SseFailureCopy {
  if (status === null) {
    return {
      message: "Can't reach the server. Live updates are paused — check your connection.",
      advice: 'wait',
    };
  }
  if (status === 403) {
    return {
      message: "You don't have access to these live updates (403).",
      advice: 'access',
    };
  }
  if (status === 404) {
    // The overwhelmingly likely cause is a deploy: this SPA build is asking for a stream
    // URL the new backend no longer serves. Say the actionable thing, not just the code.
    return {
      message:
        'Live updates unavailable — the server no longer recognizes this stream (404). ' +
        'This usually means the app was updated; reload to get the current version.',
      advice: 'reload',
    };
  }
  if (status === 406) {
    // 406 on an SSE route means content negotiation failed — the route exists but won't
    // serve text/event-stream to us. Same user-visible remedy as 404, different cause.
    return {
      message:
        'Live updates unavailable — the server rejected the stream format (406). ' +
        'Reload the page; if it persists the backend needs attention.',
      advice: 'reload',
    };
  }
  if (status >= 500) {
    return {
      message: `The server is temporarily unavailable (${status}). Live updates will resume automatically.`,
      advice: 'wait',
    };
  }
  // Any other 4xx. Generic, but still carries the code — which is the whole point of the
  // probe: before A1EE-364 the UI could not name the status at all.
  return {
    message: `Live updates unavailable (${status}). Reload the page to try again.`,
    advice: 'reload',
  };
}
